import { useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  CardHeader,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../../store/dashboardAdvertise/dashboardAdvertiseSlice';
import Loader from '../../../components/Loader';
import RefreshIcon from '@mui/icons-material/Refresh';
import Label from '../../../components/Label';
import { findDashboardAdvertise } from '../../../store/dashboardAdvertise/api';
import { AdvertiseStatus } from '../../../enum';
import { DateFormat, fDateLine, fTimeLine } from '../../../utils/formatTime';
import { selectAdvertiseStatusColor } from '../../../utils/selectAdvertiseStatusColor';
import { Link } from 'react-router-dom';
import { encryptString } from '../../../utils/url';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const DashboardAdvertiseTable = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.dashboardAdvertise);

  useEffect(() => {
    dispatch(
      findDashboardAdvertise({
        limit,
        skip,
        status: AdvertiseStatus.submit,
      })
    );
  }, [dispatch, limit, skip]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleRefresh = () => {
    dispatch(
      findDashboardAdvertise({
        limit,
        skip,
        status: AdvertiseStatus.submit,
      })
    );
  };

  return (
    <Card>
      <CardHeader
        title={'Submitted Advertise'}
        action={
          <IconButton
            aria-label='settings'
            onClick={() => handleRefresh()}
          >
            <RefreshIcon />
          </IconButton>
        }
        sx={{ mb: 2 }}
      />

        <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
          <Table size={'medium'} sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell align='center'>{'Title'}</TableCell>
                <TableCell align='center'>{'User'}</TableCell>
                <TableCell align='center'>{'Price'}</TableCell>
                <TableCell align='center'>{'Start Date'}</TableCell>
                <TableCell align='center'>{'End Date'}</TableCell>
                <TableCell align='center'>{'Status'}</TableCell>
                <TableCell align='center'>
                  <div>
                    <div style={{ whiteSpace: 'nowrap' }}>{'Created Date'}</div>
                    <div style={{ whiteSpace: 'nowrap' }}>{'Time'}</div>
                  </div>
                </TableCell>
                <TableCell align='center'>{'Action'}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow hover>
                  <TableCell
                    colSpan={8}
                    align='center'
                  >
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : data?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align='center'
                  >
                    {'No record found'}
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((advertise) => (
                  <TableRow key={advertise.id}>
                    <TableCell align='center'>{advertise?.title}</TableCell>
                    <TableCell align='center'>
                      {advertise?.createdByUser?.name}
                    </TableCell>
                    <TableCell align='center'>{advertise?.price}</TableCell>
                    <TableCell align='center'>
                      {advertise?.startDate
                        ? DateFormat(advertise?.startDate)
                        : '-'}
                    </TableCell>
                    <TableCell align='center'>
                      {advertise?.endDate
                        ? DateFormat(advertise?.endDate)
                        : '-'}
                    </TableCell>
                    <TableCell align='center'>
                      <Label
                        variant='ghost'
                        color={selectAdvertiseStatusColor(advertise?.status)}
                      >
                        {advertise?.status}
                      </Label>
                    </TableCell>
                    <TableCell align='center'>
                      {advertise?.createdAt ? (
                        <Box sx={{ whiteSpace: 'nowrap' }}>
                          <Box component="span" sx={{ display: 'block' }}>
                            {fDateLine(advertise.createdAt)}
                          </Box>
                          <Box component="span" sx={{ display: 'block' }}>
                            {fTimeLine(advertise.createdAt)}
                          </Box>
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell align='center'>
                      <IconButton aria-label='visibility'>
                        <Link
                          to={`/advertise/view/${encryptString(
                            advertise?.id ?? ''
                          )}`}
                          style={{ color: '#808080' }}
                        >
                          <RemoveRedEyeIcon />
                        </Link>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component='div'
          count={totalRecords}
          rowsPerPage={limit}
          page={Math.min(Math.round(totalRecords / limit), currentPage)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </Card>
  );
};

export default DashboardAdvertiseTable;
